import { UserRole } from '../types/auth';

interface ApiLoginUser {
  id: string;
  tenantId: string;
  nome: string;
  email: string;
  role: string;
  permissions: string[];
}

interface ApiLoginResponse {
  accessToken: string;
  tokenType: 'Bearer';
  expiresIn: number;
  user: ApiLoginUser;
}

interface ApiMfaRequiredResponse {
  mfaRequired: true;
  mfaChallengeToken: string;
  expiresIn: number;
  user: Omit<ApiLoginUser, 'role' | 'permissions'>;
}

export interface AuthenticatedUser {
  id: string;
  tenantId?: string;
  nome: string;
  email: string;
  role: UserRole;
  permissions: string[];
}

export interface LoginResult {
  mfaRequired?: false;
  accessToken: string;
  user: AuthenticatedUser;
}

export interface MfaRequiredResult {
  mfaRequired: true;
  mfaChallengeToken: string;
  user: AuthenticatedUser;
}

interface ApiErrorResponse {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export class AuthApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly isNetworkError = false
  ) {
    super(message);
  }
}

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000/api/v1';

function mapRole(role: string): UserRole {
  if (role.includes('admin')) {
    return 'admin';
  }

  if (role.includes('gestor')) {
    return 'gestor';
  }

  return 'operador';
}

async function readError(response: Response): Promise<ApiErrorResponse> {
  try {
    return (await response.json()) as ApiErrorResponse;
  } catch {
    return {
      code: 'AUTH_HTTP_ERROR',
      message: 'Não foi possível autenticar.',
    };
  }
}

export async function loginWithApi(
  email: string,
  password: string,
  mfa?: { challengeToken: string; code: string }
): Promise<LoginResult | MfaRequiredResult> {
  let response: Response;

  try {
    response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(
        mfa
          ? { email, password, mfaChallengeToken: mfa.challengeToken, mfaCode: mfa.code }
          : { email, password }
      ),
    });
  } catch {
    throw new AuthApiError(
      'AUTH_API_INDISPONIVEL',
      'API indisponível.',
      true
    );
  }

  if (!response.ok) {
    const error = await readError(response);
    throw new AuthApiError(error.code, error.message);
  }

  const data = (await response.json()) as ApiLoginResponse | ApiMfaRequiredResponse;

  if ('mfaRequired' in data && data.mfaRequired) {
    return {
      mfaRequired: true,
      mfaChallengeToken: data.mfaChallengeToken,
      user: {
        id: data.user.id,
        tenantId: data.user.tenantId,
        nome: data.user.nome,
        email: data.user.email,
        role: 'operador',
        permissions: [],
      },
    };
  }

  const loginData = data as ApiLoginResponse;
  return {
    mfaRequired: false,
    accessToken: loginData.accessToken,
    user: {
      id: loginData.user.id,
      tenantId: loginData.user.tenantId,
      nome: loginData.user.nome,
      email: loginData.user.email,
      role: mapRole(loginData.user.role),
      permissions: loginData.user.permissions,
    },
  };
}
